/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import { Injectable, effect } from "@angular/core";
import type { ItemsService } from "@app/@classes/abstract/items-service-interface";
import { ItemsTableServiceBase } from "@app/@classes/abstract/items-table-service";
import type { Tables } from "@app/@classes/supabase";

const TABLE = "hidden_fields";
type T = typeof TABLE;

@Injectable({
	providedIn: "root",
})
export class HiddenFieldsService
	extends ItemsTableServiceBase<Tables<T>>
	implements ItemsService<Tables<T>> {
	public hiddenFields: Tables<T>[] = [];

	constructor() {
		super(TABLE, { hasTenant: true });

		effect(() => {
			const tenantId = this.authStateService.currentTenantId();
			if (tenantId) {
				this.getHiddenFields(tenantId);
			}
		});
	}

	public getHiddenFields(tenantId: string) {
		this.getMany({
			filters: {
				tenant: {
					active: true,
					column: "tenant_id",
					operator: "eq",
					value: tenantId,
				},
				isHidden: {
					active: true,
					column: "is_hidden",
					operator: "eq",
					value: true,
				},
			},
		}).subscribe((data) => {
			this.hiddenFields = data;
		});
	}

	public filterByTablename(tableNames: string[]) {
		const hiddenFieldsFiltered: string[] = [];
		for (const field of this.hiddenFields) {
			if (tableNames.includes(field.table_name)) {
				hiddenFieldsFiltered.push(field.column_name);
			}
		}
		return hiddenFieldsFiltered;
	}

	public async insertNewHiddenField(columnName: string, tableName: string) {
		const { data, error } = await this.supabaseClient
			.from("hidden_fields")
			.select("id, is_hidden")
			.eq("tenant_id", this.tenantId() ?? "")
			.eq("column_name", columnName)
			.eq("table_name", tableName);
		if (error) throw error;
		if (data.length === 0) {
			const { data: insert, error: insertError } = await this.supabaseClient
				.from("hidden_fields")
				.insert([
					{
						tenant_id: this.tenantId() ?? "",
						table_name: tableName,
						column_name: columnName,
						is_hidden: true,
					},
				])
				.select();
			if (insertError) throw insertError;
			return insert;
		}
		if (data[0].is_hidden) {
			throw { message: "Field is already hidden" };
		}
		const { data: update, error: updateError } = await this.supabaseClient
			.from("hidden_fields")
			.update({ is_hidden: true })
			.eq("id", data[0].id)
			.select();
		if (updateError) throw updateError;
		return update;
	}

	public async removeHiddenField(columnName: string, tableName: string) {
		const { data, error } = await this.supabaseClient
			.from("hidden_fields")
			.select("id, is_hidden")
			.eq("tenant_id", this.tenantId() ?? "")
			.eq("column_name", columnName)
			.eq("table_name", tableName);
		if (error) throw error;
		if (data.length === 0) {
			throw { message: "No hidden field" };
		}
		if (data[0].is_hidden === false) {
			throw { message: "Field is already visible" };
		}
		this.supabaseClient
			.from("hidden_fields")
			.update({ is_hidden: false })
			.eq("id", data[0].id);
	}

	defaultItem(): Tables<T> {
		return {
			id: this.randomUUID(),
			tenant_id: "",
			table_name: "",
			column_name: "",
			is_hidden: false,
			created_at: null,
			created_by: null,
			updated_at: null,
			updated_by: null,
		};
	}
}
